<script lang="ts" setup>
import { ref, onMounted, reactive, watch, onUnmounted } from 'vue';
import { PCheckbox, IconCalendarSm as CalendarIcon } from '@profeat/ui-kit';
import { onClickOutside, onKeyStroke, watchDebounced } from '@vueuse/core';
import dayjs from 'dayjs';

import { customerConstants } from '~/components/customers/helper';
import { ContactField } from '~/entities/contacts';
import {
  EMPTY_DATE,
  FilterBuilder,
} from '~/components/customers/filter-builder';

const {
  FILTER_DATE_DISPLAY_FORMAT,
  FILTER_DATE_FORMAT,
  DEFAULT_FILTER_DEBOUNCE,
} = customerConstants;

const props = defineProps<{
  field: ContactField;
  filterBuilder: FilterBuilder;
  refWrap: HTMLElement;
  filterValue?: {
    dateFrom: null | string;
    dateTo: null | string;
    checkbox: boolean;
  };
  changed: boolean
}>();
const emit = defineEmits<{
  (event: 'applyFilter', visible?: boolean, withoutCall?: boolean): void;
  (event: 'resetFilter', withoutCall?: boolean): void;
  (event: 'clickOutside'): void;
  (event: 'update:changed', val: boolean): void;
  (
    event: 'setFilterValue',
    payload: {
      dateFrom: null | string;
      dateTo: null | string;
      checkbox: boolean;
    },
  ): void;
}>();

const filterRef = ref(null);
const dateCheckbox = ref(false);
const alreadyFiltered = ref(false);

type DateRange = { from: string | null; to: string | null };

const dateRange = reactive<DateRange>({
  from: null,
  to: null,
});

const dropdownCalendar = reactive({
  from: false,
  to: false,
});

const dataCalendar = reactive<DateRange>({
  from: null,
  to: null,
});

const dateIsValid = () => {
  if (dateRange.from === null && dateRange.to === null) return false;

  const fromDateCondition =
    dateRange.from === null
      ? true
      : dayjs(dateRange.from, FILTER_DATE_DISPLAY_FORMAT).isValid();
  const toDateCondition =
    dateRange.to === null
      ? true
      : dayjs(dateRange.to, FILTER_DATE_DISPLAY_FORMAT).isValid();

  return fromDateCondition && toDateCondition;
};

const selectDate = (value: any, type: 'from' | 'to') => {
  dateRange[type] = dayjs(value).format(FILTER_DATE_DISPLAY_FORMAT);
  dropdownCalendar[type] = false;
};

const emitFilter = () => {
  emit('applyFilter');
  emit('setFilterValue', {
    checkbox: dateCheckbox.value,
    dateFrom: dateRange.from,
    dateTo: dateRange.to,
  });
  alreadyFiltered.value = true;
};
const setToFilter = () => {
  const isDateValid = dateIsValid();

  if (props.filterValue?.checkbox !== dateCheckbox.value) {
    props.filterBuilder.remove(props.field.id);

    if (dateCheckbox.value) {
      props.filterBuilder.find(props.field.id, EMPTY_DATE);
      emitFilter();
      return;
    }

    if (!dateRange.from && !dateRange.to) {
      emitFilter();
      return;
    }
  }

  if (isDateValid) {
    if (dateRange.from) {
      props.filterBuilder.gte(
        props.field.id,
        dayjs(dateRange.from, FILTER_DATE_DISPLAY_FORMAT).format(),
      );
    }
    if (dateRange.to) {
      props.filterBuilder.lte(
        props.field.id,
        dayjs(dateRange.to, FILTER_DATE_DISPLAY_FORMAT).format(),
      );
    }

    emitFilter();
  } else if(!dateRange.from && !dateRange.to && (dateRange.from !== props.filterValue.dateFrom || dateRange.to !== props.filterValue.dateTo)) {
      emit('resetFilter', false)
  } else if(dateRange.from || dateRange.to){
    if (!dayjs(dateRange.from, FILTER_DATE_DISPLAY_FORMAT).isValid() && dateRange.from) {
      dateRange.from = null
      emit('update:changed', false);
    }
    if (!dayjs(dateRange.to, FILTER_DATE_DISPLAY_FORMAT).isValid() && dateRange.to) {
      dateRange.to = null
      emit('update:changed', false);
    }
  }
};

onKeyStroke(['Enter'], () => {
  setToFilter();
});

onClickOutside(
  () => props.refWrap,
  () => {
    setToFilter();
    emit('clickOutside');
  },
);

watch(
  [dateRange, dateCheckbox],
  ([dateValue, checkbox]) => {
    alreadyFiltered.value = false;
    if(dateValue.to === ''){
      dateValue.to = null
    }
    if(dateValue.from === ''){
      dateValue.from = null
    }
    if (dayjs(dateRange.from, FILTER_DATE_DISPLAY_FORMAT).isValid()) {
      dataCalendar.from = dayjs(
        dateRange.from,
        FILTER_DATE_DISPLAY_FORMAT,
      ).format(FILTER_DATE_FORMAT);
    }
    if (dayjs(dateRange.to, FILTER_DATE_DISPLAY_FORMAT).isValid()) {
      dataCalendar.to = dayjs(dateRange.to, FILTER_DATE_DISPLAY_FORMAT).format(
        FILTER_DATE_FORMAT,
      );
    }

    const changed = !!dateValue.from || !!dateValue.to || checkbox;

    emit('update:changed', changed);

    if (checkbox === false) props.filterBuilder.remove(props.field.id);
  },
  { deep: true },
);

watchDebounced(
  dateRange,
  () => {
    if (alreadyFiltered.value) return;

    setToFilter();
  },
  { debounce: DEFAULT_FILTER_DEBOUNCE * 15, deep: true },
);
watchDebounced(
  dateCheckbox,
  () => {
    if (alreadyFiltered.value) return;

    setToFilter();
  },
  { debounce: DEFAULT_FILTER_DEBOUNCE * 5 },
);

onMounted(() => {
  const filter = props.filterValue;

  if (filter) {
    const from = filter.dateFrom;
    const to = filter.dateTo;
    const checkbox = filter.checkbox;

    if (checkbox) dateCheckbox.value = true;
    if (from) dateRange.from = from;
    if (to) dateRange.to = to;
  }

  emit('setFilterValue', {
    checkbox: dateCheckbox.value,
    dateFrom: dateRange.from,
    dateTo: dateRange.to,
  });
});
onUnmounted(() => {
  alreadyFiltered.value = true;
})
</script>

<template>
  <div class="date-filter" ref="filterRef">
    <div class="fields">
      <PDropdown
        v-model="dropdownCalendar.from"
        :class="{ disabled: dateCheckbox }"
        :close-on-self-click="false"
        :placement="'bottom'"
      >
        <template #activator="{ setReference }">
          <PInput
            :ref="setReference"
            class="input"
            v-model="dateRange.from"
            type="text"
            :mask="`##.##.####`"
            placeholder="ДД.ММ.ГГГГ"
            :disabled="dateCheckbox"
            autofocus
          >
            <template #right>
              <CalendarIcon @click="dropdownCalendar.from = true" class="text-[#91A0AE] icon cursor-[pointer]" />
            </template>
          </PInput>
        </template>
        <PCalendar v-model="dataCalendar.from" format="ISO" @select-date="selectDate($event, 'from')" />
      </PDropdown>

      <hr class="divider" />

      <PDropdown
        v-model="dropdownCalendar.to"
        :class="{ disabled: dateCheckbox }"
        :close-on-self-click="false"
        :placement="'bottom'"
      >
        <template #activator="{ setReference }">
          <PInput
            :ref="setReference"
            class="input"
            v-model="dateRange.to"
            type="text"
            :mask="`##.##.####`"
            placeholder="ДД.ММ.ГГГГ"
            :disabled="dateCheckbox"
          >
            <template #right>
              <CalendarIcon @click="dropdownCalendar.to = true" class="text-[#91A0AE] icon cursor-[pointer]" />
            </template>
          </PInput>
        </template>
        <PCalendar v-model="dataCalendar.to" format="ISO" @select-date="selectDate($event, 'to')" />
      </PDropdown>
    </div>

    <div class="checkbox">
      <PCheckbox v-model="dateCheckbox" label="Нет даты" text-size="sm" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.calendar-wrapper {
  display: flex;
  flex-direction: column;
  grid-gap: 12px;
}

.fields {
  display: grid;
  grid-template-columns: 1fr 16px 1fr;
  align-items: center;
  gap: 8px;

  :deep(.dropdown) {
    width: 328px;
  }

  .divider {
    box-sizing: border-box;
    width: 16px;
    height: 1px;
    background: var(--common-black, #2b3238);
    outline: none;
    border: none;
  }
}

.date-filter {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.disabled {
  pointer-events: none;
}
</style>
